import { CaretDownFilled } from '@ant-design/icons';
import { Button } from 'antd';
import Tooltip from '../common/Tooltip';
import { cloneElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useModalOpener } from '../../hooks';
import { clampListHorizontalShift, isLightColor, navbarButtonStyle } from '../../utils/styles';
import ColorButton from '../TextStylingComponent/ColorButton';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import { BORDER_GRAY, PANEL_LIGHT_BG, PRIMARY_DARK_ADMIN } from '../../constants/constants';

// Marge minimale entre la liste et un bord de la fenêtre.
const VIEWPORT_MARGIN = 8;
// Au-dessus de tout le chrome de l'éditeur (navbar 1100, barre flottante 9998,
// drawers 10002, popups antd 10100) : la liste est portée sur <body>, elle doit
// passer par-dessus le contexte depuis lequel on l'ouvre.
const LIST_Z_INDEX = 10200;

export const NavbarListButton = ({
  title,
  disabled,
  icon,
  onListElementClick,
  listElements,
  selectedElement,
  children,
  hidden,
  isOpen,
  onToggleOpen,
  customStyle = {},
  colorInIcon = false
}) => {

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [displayList, setDisplayList] = useModalOpener(false);
  const listRef = useRef(null);
  const listDisplayRef = useRef(null);
  // Position calculée après mesure : masquée au premier rendu, sinon la liste
  // clignote en haut à gauche de la fenêtre avant d'être placée.
  const [listPosition, setListPosition] = useState(null);

  // Utiliser l'état contrôlé externe si fourni, sinon utiliser l'état local
  const isListOpen = isOpen !== undefined ? isOpen : displayList;
  const setIsListOpen = onToggleOpen || setDisplayList;

  // La liste est portée sur <body> : un clic dedans est « hors » du bouton pour
  // le DOM, il ne doit pourtant pas la fermer.
  useEffect(() => {
    if (!isListOpen) return undefined;
    const handleMouseDown = (event) => {
      if (listRef.current?.contains(event.target)) return;
      if (listDisplayRef.current?.contains(event.target)) return;
      setIsListOpen(false);
      setTooltipVisible(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isListOpen, setIsListOpen]);

  // `colorInIcon` : la couleur sélectionnée remplace une couche du duotone
  // (icône DuotoneLightIcon, sinon l'icône s'en charge) — la 2e
  // (grise) par défaut, la 1re avec `colorInIcon='primary'`. Sans couleur (ou
  // « transparent »), l'icône garde sa teinte d'origine. Un contour très léger,
  // réservé aux couleurs claires, évite qu'un blanc ne disparaisse sur le fond
  // blanc du bouton (sur les autres couleurs il ne ferait que les noircir).
  const iconColor = selectedElement?.style;
  // Une icône qui n'est pas un DuotoneLightIcon (ex. la lettre A de ColorControl)
  // montre sa couleur elle-même.
  const showColorInIcon = !!colorInIcon && !!iconColor && iconColor !== 'transparent';
  const iconLayerProps = colorInIcon === 'primary'
    ? { primaryColor: iconColor, className: 'duotone-primary-colored' }
    : { secondaryColor: iconColor, secondaryOpacity: 1, className: 'duotone-secondary-colored' };
  const iconDisplay = showColorInIcon && icon?.type === DuotoneLightIcon
    ? cloneElement(icon, {
      ...iconLayerProps,
      style: {
        ...icon.props.style,
        ...(isLightColor(iconColor) && { filter: 'drop-shadow(0 0 0.5px rgba(0, 0, 0, 0.35))' }),
      },
    })
    : icon;

  const buttonDisplay = (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      width: '100%',
      height: '100%',
      opacity: disabled ? 0.3 : 1,
    }}>
      {iconDisplay}
    </div>
  )

  // Placement de la liste à l'ouverture : elle bascule au-dessus du bouton
  // quand elle déborderait en bas, et se décale horizontalement pour rester
  // dans son conteneur. Centrée sur le bouton sans bornage latéral, elle était
  // coupée dès que le bouton s'approchait d'un bord du panel de droite, qui
  // rogne ce qui dépasse en largeur (overflow hidden sur X) : palette tronquée
  // à gauche pour une image, à droite pour une forme.

  // Placement de la liste : centrée sous son bouton, puis ramenée dans la
  // fenêtre — jamais un pixel dehors, jamais rognée. Elle vivait auparavant en
  // absolu dans le bouton, donc à la merci du premier ancêtre qui coupe ce qui
  // dépasse (le panel de droite est en overflow hidden sur X) : la palette de
  // couleurs sortait tronquée à gauche pour une image, à droite pour une forme.
  const placeList = useCallback(() => {
    const button = listRef.current?.getBoundingClientRect();
    const list = listDisplayRef.current?.getBoundingClientRect();
    if (!button || !list) return;

    const maxHeight = window.innerHeight - VIEWPORT_MARGIN * 2;
    const height = Math.min(list.height, maxHeight);
    const centeredLeft = button.left + button.width / 2 - list.width / 2;
    const left = centeredLeft + clampListHorizontalShift({
      listLeft: centeredLeft,
      listRight: centeredLeft + list.width,
      boundsLeft: 0,
      boundsRight: window.innerWidth,
      margin: VIEWPORT_MARGIN,
    });

    // Sous le bouton par défaut ; au-dessus s'il n'y a pas la place en bas mais
    // qu'il y en a en haut. Dans tous les cas la liste reste dans la fenêtre.
    const roomBelow = window.innerHeight - button.bottom - VIEWPORT_MARGIN;
    const roomAbove = button.top - VIEWPORT_MARGIN;
    const openUpwards = roomBelow < height && roomAbove > roomBelow;
    const wantedTop = openUpwards ? button.top - VIEWPORT_MARGIN - height : button.bottom + VIEWPORT_MARGIN;
    const top = Math.min(
      Math.max(wantedTop, VIEWPORT_MARGIN),
      Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN)
    );

    setListPosition({
      top,
      left,
      maxHeight,
      openUpwards,
      // Le caret suit le bouton même quand la liste a été ramenée dans la
      // fenêtre, sinon il pointe à côté.
      caretOffset: button.left + button.width / 2 - left,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isListOpen) {
      setListPosition(null);
      return undefined;
    }
    placeList();

    // `capture` : le scroll d'un conteneur interne (panel de droite, page
    // infinie) ne remonte pas jusqu'à window en phase de bouillonnement.
    window.addEventListener('scroll', placeList, true);
    window.addEventListener('resize', placeList);
    const observer = new ResizeObserver(placeList);
    if (listDisplayRef.current) observer.observe(listDisplayRef.current);

    return () => {
      window.removeEventListener('scroll', placeList, true);
      window.removeEventListener('resize', placeList);
      observer.disconnect();
    };
  }, [isListOpen, placeList]);

  const listContent = isListOpen && ReactDOM.createPortal(
    <div
      ref={listDisplayRef}
      data-outside-ignore="true"
      style={{
        position: 'fixed',
        top: listPosition?.top ?? 0,
        left: listPosition?.left ?? 0,
        zIndex: LIST_Z_INDEX,
        display: 'flex',
        flexDirection: listPosition?.openUpwards ? 'column-reverse' : 'column',
        alignItems: 'flex-start',
        // Tant que la taille réelle n'est pas connue, la liste est rendue hors
        // écran pour être mesurée sans se montrer mal placée.
        visibility: listPosition ? 'visible' : 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <CaretDownFilled
        className='dislayed-caret'
        style={{
          color: PRIMARY_DARK_ADMIN,
          marginLeft: Math.max(0, (listPosition?.caretOffset ?? 0) - 6),
          transform: listPosition?.openUpwards ? 'rotate(180deg)' : undefined,
        }}
      />
      <div
        style={{
          display: 'grid',
          // Fond opaque, cadre et ombre : la liste flotte au-dessus du panel de
          // droite et n'avait aucun fond — les libellés et les champs du panel
          // se lisaient entre les boutons.
          backgroundColor: PANEL_LIGHT_BG,
          border: `1px solid ${BORDER_GRAY}`,
          borderRadius: 6,
          boxShadow: '0 6px 16px rgba(11, 34, 57, 0.16)',
          padding: 4,
          rowGap: 2,
          // Les boutons font 32px de large, un intitulé de groupe est plus
          // large : sans ça la colonne de grille prenait la largeur du texte et
          // les boutons restaient collés à gauche, en escalier.
          justifyItems: 'center',
          // Liste plus haute que la fenêtre (beaucoup de couleurs, petit
          // écran) : elle défile au lieu de sortir de l'écran.
          maxHeight: listPosition?.maxHeight,
          overflowY: 'auto',
        }}
        className='dislayed-list'
      >
        {children}
        {!children && listElements && listElements.map((element, index) => (
          <ColorButton
            key={element.label}
            active={
              element.style === selectedElement?.style
            }
            label={element.label}
            name={element.name}
            inheritStyle={element.inheritStyle}
            onToggle={(elementValue) => onListElementClick(elementValue)}
            colorStyle={element.style}
            positionStyle={{
              gridColumn: Math.ceil((index + 1) / 5),
              gridRow: (index % 5) + 1,
            }}
            size='32px'
          />
        ))}
      </div>
    </div>,
    document.body
  );

  return (
    <Tooltip
      // `open` piloté : antd ne masque alors plus l'infobulle sans titre (bulle noire
      // vide au survol pour les boutons qui n'en passent pas, ex. drawBoxBgColor).
      open={tooltipVisible && !!title}
      placement='bottom'
      mouseLeaveDelay='0'
      title={title}
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
    >
      {/* Ce conteneur épouse le bouton : c'est lui que l'infobulle prend pour
          repère, et il est aussi le repère de la liste. Étiré — dans une ligne
          de formulaire il prendrait toute la largeur restante — l'une et l'autre
          se posaient au milieu de cette largeur, loin du bouton. */}
      <div
        data-outside-ignore="true"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          width: 'fit-content'
        }}
        ref={listRef}
      >
        <div>
          <Button
            onMouseEnter={() => setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
            style={{ ...navbarButtonStyle, ...customStyle }}
            disabled={disabled}
            hidden={hidden}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsListOpen(!isListOpen)
              setTooltipVisible(false)
            }}
          >
            {buttonDisplay}
          </Button>
        </div>
        {listContent}
      </div>
    </Tooltip>
  );
};
